import { InputAdornment } from '@mui/material'
import { DateTimeValidationError, LocalizationProvider, PickerChangeHandlerContext } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import {
  DateRangePicker,
  DateRangePickerProps,
  DateRangeValidationError,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'
import React, { useState } from 'react'

import { useComplatibleLocale } from '../../hooks/useCompatibleLocale'
import { useRtlLocaleCheck } from '../../hooks/useRtlLocaleCheck'
import { styled } from '../../theme'
import { MsdkStyledComponent } from '../../utils'
import { ButtonIcon } from '../Button'
import { IconSelector } from '../Icons'
import { DateRange } from './RangeDatePicker'

const StyledDateRangePicker: MsdkStyledComponent<DateRangePickerProps<any>> = styled(DateRangePicker)`
  .MuiInputBase-root {
    border-radius: 12px;
    padding-inline: 6px;
    width: 100%;
    background: #fff;
    box-shadow: 0 2px 4px -1px #0a227129;
  }
  .MuiInputBase-input {
    padding: 9px;
    width: 100%;
    color: #0342c9;
    user-select: none !important;
    cursor: pointer !important;
  }
  .MuiFormControl-root {
    width: 100%;
    border: 0.5px solid #c2c3cc;
    border-radius: 12px;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 0.5px solid #c2c3cc;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: #0342c9 !important;
  }

  * {
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
  }
`

type Props = {
  disabled?: boolean
  label: React.ReactNode
  locale?: string
  onChange: (value: DateRange | undefined, context?: PickerChangeHandlerContext<DateTimeValidationError>) => void
  value: DateRange | undefined
}

export function WeekRangeDatePicker(props: Props) {
  const { disabled, locale = DateTime.local().locale, onChange, value } = props

  const [open, setOpen] = useState<boolean>(false)
  const compatibleLocale = useComplatibleLocale(locale)
  const isRtl = useRtlLocaleCheck(locale)

  function handleDateChange(
    newDates: DateRange | undefined,
    _context?: PickerChangeHandlerContext<DateRangeValidationError>,
  ) {
    if (newDates) {
      /**
       * The DateRangePicker doesn't provide information about which date (start or end) was changed. To determine the week range, we can check which of the two dates is different from the current value. If both are different or both are the same, we can default to using the first available date (usually start date) as the reference point for calculating the week range.
       */
      let clickedDate: DateTime | null = null

      if (newDates[0] && (!value?.[0] || !newDates[0].equals(value[0]))) {
        // Start date changed
        clickedDate = newDates[0]
      } else if (newDates[1] && (!value?.[1] || !newDates[1].equals(value[1]))) {
        // End date changed
        clickedDate = newDates[1]
      } else {
        // Fallback: use first available date
        clickedDate = newDates[0] || newDates[1]
      }

      if (clickedDate) {
        const startOfWeek = clickedDate.startOf('week')
        const endOfWeek = clickedDate.endOf('week')
        onChange?.([startOfWeek, endOfWeek])
        setOpen(false)
      }
    }
  }

  function handleOpen() {
    setOpen(true)
  }

  function handleClose() {
    setOpen(false)
  }

  function handleIconClick() {
    setOpen((prev) => !prev)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={compatibleLocale}>
      <StyledDateRangePicker
        disabled={disabled}
        slots={{
          field: SingleInputDateRangeField,
        }}
        slotProps={{
          field: {
            readOnly: true,
            disabled,
          },
          textField: {
            onClick: () => handleIconClick(),
            InputProps: {
              readOnly: true,
              onMouseDown: (e) => e.preventDefault(),
              sx: {
                userSelect: 'none !important',
                cursor: 'pointer !important',
                color: '#0342c9 !important',
                '& *': {
                  userSelect: 'none !important',
                  WebkitUserSelect: 'none !important',
                  MozUserSelect: 'none !important',
                  msUserSelect: 'none !important',
                },
              },
              dir: isRtl ? 'rtl' : undefined,
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                  <ButtonIcon
                    onClick={(e) => {
                      e.stopPropagation()
                      handleIconClick()
                    }}
                  >
                    <IconSelector iconName="DateRangeOutlined" color="#0342C9" />
                  </ButtonIcon>
                </InputAdornment>
              ),
            },
          },
          actionBar: { actions: [] },
          popper: {
            sx: {
              '& [class*="MuiDateRangePickerDay-rangeIntervalPreview"]': {
                border: 'none !important',
                borderTop: 'none !important',
                borderBottom: 'none !important',
                borderLeft: 'none !important',
                borderRight: 'none !important',
              },
            },
          },
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        calendars={1}
        value={value}
        onChange={handleDateChange}
        reduceAnimations={true}
      />
    </LocalizationProvider>
  )
}
